import { useEffect, useState } from "react";

import Button from "@mui/material/Button";

import EditIcon from "@mui/icons-material/EditOutlined";

import FacilityForm from "../../../../addLocationPanel/components/FacilityForm";
import ILocation from "../../../../../types/ILocation";
import { useNitroStore } from "../../../../../store/Store";
import LocationSchema from "../../../../addLocationPanel/schemas/LocationSchema";
import NitroService from "../../../../../utils/nitro.service";
import CloseIcon from "../../../../../components/CloseIcon";
import UnsavedConfirmation from "../../../../../components/unsavedConfirmation";
import LoadingIcon from "../../../../../components/Loading";
import PinSelectConfirmation from "../../../../../components/PinSelectConfirmation";
import ZIndex from "../../../../../styles/zindex";
import PanelFooter from "../../../../../components/Panel/PanelFooter";
import useEscapeKeyExit from "../../../../../hooks/useEscapeKeyExit";
import { useIsMobile } from "../../../../../hooks/useIsMobile";
import { useNitroCAStore } from "../../../../../store/CompetitiveAnalysisStore";

const OverviewFacilityTab = ({
  location,
  reloadLocation,
}: // eslint-disable-next-line @typescript-eslint/no-unused-vars
{
  location: ILocation;
  reloadLocation: () => Promise<void>;
}) => {
  const isMobile = useIsMobile(768);
  const [
    selectingPinLocation,
    setSelectingPinLocation,
    pinLocation,
    setPinLocation,
    loadLocations,
    savedNotification,
    failedNotification,
    insightsEditing,
    setInsightsEditing,
    setMapType,
  ] = useNitroStore((state) => [
    state.selectingPinLocation,
    state.setSelectingPinLocation,
    state.pinLocation,
    state.setPinLocation,
    state.loadLocations,
    state.savedNotification,
    state.failedNotification,
    state.insightsEditing,
    state.setInsightsEditing,
    state.setMapType,
  ]);
  const [expandedDestinationId, setExpandedDestination, triggerRefetchMultiLocation] = useNitroCAStore(
    (state) => [state.expandedDestId, state.setExpandedDestination, state.triggerRefetchMultiLocation],
  );

  const [temporaryLocation, setTemporaryLocation] = useState<
    ILocation | undefined
  >(location);

  // Set pin location when starting editing or location is loaded
  useEffect(() => {
    if (location && location.coordinates) {
      console.info("Facility - setPinLocation", location.coordinates);
      setPinLocation({
        lat: Number(location.coordinates[0]),
        lng: Number(location.coordinates[1]),
      });
    }
    setTemporaryLocation(location);
  }, [location, insightsEditing, setPinLocation]);

  // ################################################################################################################################
  // ## VALIDATION ##################################################################################################################
  // ################################################################################################################################
  const [isValid, setIsValid] = useState(false);
  useEffect(() => {
    if (!temporaryLocation) {
      console.warn("No location");
      return setIsValid(false);
    }
    if (
      Number(temporaryLocation.coordinates[0]) > 90 ||
      Number(temporaryLocation.coordinates[0]) < -90
    ) {
      console.warn("Invalid coordinates 0");
      return setIsValid(false);
    }
    if (
      Number(temporaryLocation.coordinates[1]) > 180 ||
      Number(temporaryLocation.coordinates[1]) < -180
    ) {
      console.warn("Invalid coordinates 1");
      return setIsValid(false);
    }

    let newIsValid = LocationSchema.isValidSync(temporaryLocation);
    console.warn({ newIsValid });
    LocationSchema.validate(temporaryLocation).catch((err) => {
      console.error(err);
    });

    if (
      temporaryLocation.inboundModes.includes("RAIL") ||
      temporaryLocation.outboundModes.includes("RAIL")
    ) {
      if (
        location.zipCode !== temporaryLocation.zipCode &&
        (!temporaryLocation.stationState ||
          !temporaryLocation.carriers ||
          temporaryLocation.carriers.length === 0)
      ) {
        // Rail is enabled && zip code changed, force user to reselect rail station and carrier
        newIsValid = false;
      }
    }

    return setIsValid(newIsValid);
  }, [temporaryLocation, location]);

  const stopEditing = () => {
    setInsightsEditing(undefined);
    setSelectingPinLocation(false);
    setPinLocation(undefined);
  };

  const [isSaving, setIsSaving] = useState(false);
  const saveLocation = async () => {
    if (!temporaryLocation) return;
    console.info("Save location", { temporaryLocation });
    setIsSaving(true);
    try {
      await NitroService.updateLocation({
        id: temporaryLocation.id,
        name: temporaryLocation.name,
        owner: temporaryLocation.owner || "",

        address: temporaryLocation.address,
        city: temporaryLocation.city,
        state: temporaryLocation.state,
        zipCode: temporaryLocation.zipCode,
        country: temporaryLocation.country,
        coordinates: [
          Number(temporaryLocation.coordinates[0]),
          Number(temporaryLocation.coordinates[1]),
        ],

        uom: temporaryLocation.uom,

        railChanged: location.zipCode !== temporaryLocation.zipCode,
        railStationName: temporaryLocation.stationState || "",
        carriers: temporaryLocation.carriers.map((c) => c.id) || [],
        equipmentTypes: temporaryLocation.equipmentTypes || [],
        railInbound: temporaryLocation.inboundModes.includes("RAIL"),
        railOutbound: temporaryLocation.outboundModes.includes("RAIL"),
      });

      loadLocations({ panTo: true });
      await reloadLocation();
      savedNotification("Successfully saved facility.");
      stopEditing();
      triggerRefetchMultiLocation();
      if (expandedDestinationId) {
        setExpandedDestination({ id: expandedDestinationId, name: "" });
      }
    } catch (err) {
      console.error("Error saving location...", err);
      failedNotification("Failed to save facility.", (err as any).message);
    }
    setIsSaving(false);
  };

  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false);

  const [hasChange, setHasChange] = useState(false);
  useEffect(() => {
    let newhasChange = false;

    if (location.name !== temporaryLocation?.name) newhasChange = true;
    if (location.owner !== temporaryLocation?.owner) newhasChange = true;
    if (location.address !== temporaryLocation?.address) newhasChange = true;
    if (location.city !== temporaryLocation?.city) newhasChange = true;
    if (location.state !== temporaryLocation?.state) newhasChange = true;
    if (location.zipCode !== temporaryLocation?.zipCode) newhasChange = true;
    if (location.country !== temporaryLocation?.country) newhasChange = true;
    if (location.coordinates[0] !== temporaryLocation?.coordinates[0])
      newhasChange = true;
    if (location.coordinates[1] !== temporaryLocation?.coordinates[1])
      newhasChange = true;
    if (location.uom !== temporaryLocation?.uom) newhasChange = true;

    setHasChange(newhasChange);
  }, [location, temporaryLocation]);

  const attemptClose = () => {
    if (hasChange) {
      setCloseConfirmationOpen(true);
    } else {
      stopEditing();
    }
  };

  useEscapeKeyExit({
    active: !hasChange && insightsEditing === "Facility",
    close: stopEditing,
  });
  useEscapeKeyExit({
    active: hasChange && insightsEditing === "Facility",
    close: attemptClose,
  });

  return (
    <div
      id="overview-facility-tab"
      style={{
        flexGrow: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {closeConfirmationOpen && (
        <UnsavedConfirmation
          confirm={() => {
            setCloseConfirmationOpen(false);
            stopEditing();
          }}
          cancel={() => {
            setCloseConfirmationOpen(false);
          }}
        />
      )}

      {selectingPinLocation && (
        <>
          <div
            id="hideInsightsPanel"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: ZIndex.OpaqueBackground,
              backgroundColor: "rgba(15, 44, 82, 0.3)", // Selecting pin location
            }}
          />
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: isMobile ? 0 : 400,
              zIndex: "1000 !important",
            }}
          >
            <PinSelectConfirmation
              confirm={() => {
                setMapType("roadmap");
                if (temporaryLocation && pinLocation) {
                  console.info("Confirm", [pinLocation.lat, pinLocation.lng]);
                  setTemporaryLocation({
                    ...temporaryLocation,
                    coordinates: [pinLocation.lat, pinLocation.lng],
                  });
                  setSelectingPinLocation(false);
                }
              }}
              cancel={() => {
                setMapType("roadmap");
                if (temporaryLocation) {
                  setPinLocation({
                    lat: Number(temporaryLocation.coordinates[0]),
                    lng: Number(temporaryLocation.coordinates[1]),
                  });
                  setSelectingPinLocation(false);
                }
              }}
            />
          </div>
        </>
      )}

      <div
        className="insightsPanelPadding"
        style={{
          height: 32.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 10,
        }}
      >
        <div
          id='facility-name'
          style={{
            color: "#0F2C52",
            fontWeight: 700,
            fontSize: 18,
            textTransform: "uppercase",
          }}
        >
          Facility
        </div>
        {insightsEditing !== "Facility" && (
          <Button
            id="facilityEditButton"
            variant="contained"
            onClick={() => setInsightsEditing("Facility")}
            style={{ display: "flex", gap: 4 }}
          >
            <EditIcon style={{ fontSize: 18 }} />
            Edit
          </Button>
        )}
        {!isSaving && insightsEditing === "Facility" && (
          <CloseIcon onClick={attemptClose} />
        )}
      </div>
      {temporaryLocation && !isSaving && (
        <div
          className="insightsPanelPadding"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            paddingBottom: 20,
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          {/* <fieldset disabled={true}> */}
          <FacilityForm
            disabled={insightsEditing !== "Facility"}
            originalLocation={location}
            location={temporaryLocation}
            setLocation={setTemporaryLocation}
            startSelectingCoordinates={() => {
              setSelectingPinLocation(true);
            }}
          />
          {/* </fieldset> */}
        </div>
      )}

      {temporaryLocation && isSaving && (
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingIcon color="#005CA6" height={100} width={100} />
        </div>
      )}

      {/* FOOTER */}
      {insightsEditing === "Facility" && (
        <PanelFooter>
          <Button
            id="facilityUpdateButton"
            variant="contained"
            fullWidth
            onClick={saveLocation}
            disabled={!isValid || !hasChange || isSaving}
          >
            UPDATE INFORMATION
          </Button>
        </PanelFooter>
      )}
      {/* END FOOTER */}
    </div>
  );
};

export default OverviewFacilityTab;
